import { axiosInstance } from "./axios";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";

// Jednolity rzeczowy wykaz akt (JRWA) — port klasyfikacji z Magistratu. Wykaz jest per
// firma i serwowany z bazy (BE zasila pusty wykaz oficjalnym JRWA gminy przy pierwszym
// odczycie). Sygnatura teczki SYMBOL.KOD.NR.ROK nadawana jest przez BE przy założeniu
// teczki (numer ciągły w ramach firmy, roku i kodu).

export const jrwaQueryKeys = {
  list: "jrwa.list",
  preview: (classificationId: string, symbol: string) => [
    "jrwa.preview",
    classificationId,
    symbol,
  ],
};

/** Rodzaj oficjalnego wykazu (BE: JrwaSeedType). */
export enum EJrwaSeedType {
  Gmina = 1,
  Powiat = 2,
  Wojewodztwo = 3,
}

export interface IJrwaClassification {
  Id: string;
  ParentId?: string | null;
  Code: string;
  Description: string;
  /** Pozycja oficjalnego wykazu — kod i hasło niezmienne. */
  IsReadOnly: boolean;
  IsArchived: boolean;
  /** Kwantum.Enums.ArchiveType: 0 NotSet, 1 A, 2 B, 3 B3, 4 B5, 5 B6, 6 B10, … */
  ArchiveType?: number | null;
  CasesCount: number;
  /**
   * „Pracownicy prowadzą teczki wspólnie" (Magistrat: Procedure.IsShared) — wszyscy
   * prowadzący z `UserIds` widzą teczki pod tym kodem i mają do nich pełny dostęp.
   * Bez flagi teczki są prywatne: widzi je wyłącznie właściciel.
   */
  IsShared: boolean;
  /** Pracownicy prowadzący kod (UserId członków firmy; Magistrat: ProceduresUsers). */
  UserIds: string[];
}

export interface IJrwaListResponse {
  Items: IJrwaClassification[];
  CanEdit: boolean;
  HasCases: boolean;
}

export interface ISaveJrwaClassificationRequest {
  Id?: string | null;
  ParentId?: string | null;
  Code: string;
  Description: string;
  IsArchived?: boolean;
  ArchiveType?: number | null;
  /** „Pracownicy prowadzą teczki wspólnie" — dozwolone także dla pozycji oficjalnych. */
  IsShared?: boolean;
  /** Pełna lista prowadzących (zastępuje poprzednią); pominięte = bez zmian. */
  UserIds?: string[];
}

export interface ISignaturePreview {
  Signature: string;
  MajorNumber: number;
  Year: number;
  Code: string;
  DepartmentSymbol: string;
}

const getList = (): Promise<{ data: IJrwaListResponse }> =>
  axiosInstance.get("jrwa");

const useGetList = (enabled = true) =>
  useCustomQuery(jrwaQueryKeys.list, () => getList(), {
    enabled,
    staleTime: 5 * 60_000,
  });

const save = (
  payload: ISaveJrwaClassificationRequest,
): Promise<{ data: IJrwaClassification }> => axiosInstance.post("jrwa", payload);

const useSave = () => useCustomMutation(save);

const remove = (payload: {
  id: string;
  cascade?: boolean;
}): Promise<{ data: unknown }> =>
  axiosInstance.delete(`jrwa/${payload.id}`, {
    params: { cascade: payload.cascade ?? false },
  });

const useRemove = () => useCustomMutation(remove);

const reset = (type: EJrwaSeedType): Promise<{ data: IJrwaListResponse }> =>
  axiosInstance.post("jrwa/reset", { Type: type });

const useReset = () => useCustomMutation(reset);

const getSignaturePreview = (
  classificationId: string,
  departmentSymbol: string,
): Promise<{ data: ISignaturePreview }> =>
  axiosInstance.get("jrwa/signature-preview", {
    params: { classificationId, departmentSymbol },
  });

const useSignaturePreview = (
  classificationId: string | null,
  departmentSymbol: string,
) =>
  useCustomQuery(
    jrwaQueryKeys.preview(classificationId ?? "", departmentSymbol),
    () => getSignaturePreview(classificationId!, departmentSymbol),
    { enabled: !!classificationId, staleTime: 0, cacheTime: 0 },
  );

/** Etykieta pozycji jak w Magistracie: „KOD - hasło". */
export const jrwaLabel = (c: Pick<IJrwaClassification, "Code" | "Description">) =>
  `${c.Code} - ${c.Description}`;

const jrwaService = {
  getList,
  useGetList,
  save,
  useSave,
  remove,
  useRemove,
  reset,
  useReset,
  getSignaturePreview,
  useSignaturePreview,
};

export default jrwaService;
